import React, { useEffect, useState } from "react";
import { Form, Input, DatePicker, Row, Col, Select, Checkbox } from "antd";
import Map2 from "./../Map/Map2";
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import GeocodingButton from "../../components/GeocodingButton";
import axios from "axios";

const { Option } = Select;

const EventForm = props => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const isEditMode = props.isEditMode || false;

  // Multi-hazard linkage: whether this event belongs to a multi-hazard event, and the options to pick from.
  const [partOfMultiHazard, setPartOfMultiHazard] = useState(false);
  const [parentEventOptions, setParentEventOptions] = useState([]);
  const watchedType = Form.useWatch('type', form);
  const isMultiHazardEventType = watchedType === 'MULTI_HAZARD_EVENT';

  const formItemLayout = {
    labelCol: {
      sm: { span: 6 }
    },
    wrapperCol: {
      sm: { span: 16 }
    }
  };

  // TODO: REMOVE AFTER DATA MIGRATION (added 2025-01-03)
  // This mapping is temporary for migrating old country names to codes
  // Once all database records use country codes (IT, FR, etc), remove:
  // 1. This countryNameToCode object
  // 2. The normalizeCountry function
  // 3. All normalizeCountry() calls in form initialization
  // Query to check migration status: SELECT DISTINCT country FROM events WHERE LENGTH(country) > 2;
  const countryNameToCode = {
    'italy': 'IT', 'italia': 'IT',
    'france': 'FR', 'francia': 'FR',
    'germany': 'DE', 'germania': 'DE', 'deutschland': 'DE',
    'spain': 'ES', 'spagna': 'ES', 'españa': 'ES',
    'portugal': 'PT', 'portogallo': 'PT',
    'netherlands': 'NL', 'olanda': 'NL', 'paesi bassi': 'NL', 'holland': 'NL',
    'belgium': 'BE', 'belgio': 'BE', 'belgique': 'BE',
    'austria': 'AT', 'österreich': 'AT',
    'switzerland': 'CH', 'svizzera': 'CH', 'schweiz': 'CH', 'suisse': 'CH',
    'united kingdom': 'GB', 'regno unito': 'GB', 'uk': 'GB', 'great britain': 'GB',
    'ireland': 'IE', 'irlanda': 'IE', 'eire': 'IE',
    'denmark': 'DK', 'danimarca': 'DK', 'danmark': 'DK',
    'sweden': 'SE', 'svezia': 'SE', 'sverige': 'SE',
    'norway': 'NO', 'norvegia': 'NO', 'norge': 'NO',
    'finland': 'FI', 'finlandia': 'FI', 'suomi': 'FI',
    'poland': 'PL', 'polonia': 'PL', 'polska': 'PL',
    'czech republic': 'CZ', 'repubblica ceca': 'CZ', 'cechia': 'CZ', 'czechia': 'CZ',
    'hungary': 'HU', 'ungheria': 'HU', 'magyarország': 'HU',
    'romania': 'RO', 'românia': 'RO',
    'bulgaria': 'BG', 'българия': 'BG',
    'croatia': 'HR', 'croazia': 'HR', 'hrvatska': 'HR',
    'slovenia': 'SI', 'slovenija': 'SI',
    'slovakia': 'SK', 'slovacchia': 'SK', 'slovensko': 'SK',
    'greece': 'GR', 'grecia': 'GR', 'hellas': 'GR', 'ellada': 'GR',
    'georgia': 'GE', 'sakartvelo': 'GE',
    'turkey': 'TR', 'turchia': 'TR', 'türkiye': 'TR',
    'armenia': 'AM', 'hayastan': 'AM',
    'azerbaijan': 'AZ', 'azərbaycan': 'AZ'
  };

  // TODO: REMOVE AFTER DATA MIGRATION - Helper function to normalize country value
  const normalizeCountry = (country) => {
    if (!country) return null;
    
    // If it's already a valid 2-letter code, return it
    if (country.length === 2 && /^[A-Z]{2}$/i.test(country)) {
      return country.toUpperCase();
    }
    
    // Try to convert country name to code
    const normalized = country.toLowerCase().trim();
    return countryNameToCode[normalized] || country; // Return original if not found
  };

  // Initialize form with record data
  useEffect(() => {
    if (props.record.event) {
      console.log("EventForm received event data:", props.record.event);
      form.setFieldsValue({
        name: props.record.event.name,
        type: props.record.event.type,
        severity: props.record.event.severity,
        date: props.record.event.date ? dayjs(props.record.event.date) : null,
        dateEnd: props.record.event.dateEnd ? dayjs(props.record.event.dateEnd) : null,
        geometry: props.record.event.geometry,
        country: normalizeCountry(props.record.event.country), // TODO: REMOVE normalizeCountry() after migration
        state: props.record.event.state,
        county: props.record.event.county,
        town: props.record.event.town,
        hamlet: props.record.event.hamlet,
        postcode: props.record.event.postcode,
        road: props.record.event.road,
        houseNumber: props.record.event.houseNumber,
        coordinatesInferred: props.record.event.coordinatesInferred || false,
        parentEventId: props.record.event.parentEventId || null
      });
      setPartOfMultiHazard(!!props.record.event.parentEventId);
      console.log("Form values after setting:", form.getFieldsValue());
    }
  }, [props.record.event, form]);

  // Reset validation when exiting edit mode
  useEffect(() => {
    if (!isEditMode) {
      console.log("Resetting form fields due to edit mode change");
      form.resetFields();
      
      if (props.record.event) {
        form.setFieldsValue({
          name: props.record.event.name,
          type: props.record.event.type,
          severity: props.record.event.severity,
          date: props.record.event.date ? dayjs(props.record.event.date) : null,
          dateEnd: props.record.event.dateEnd ? dayjs(props.record.event.dateEnd) : null,
          geometry: props.record.event.geometry,
          country: normalizeCountry(props.record.event.country), // TODO: REMOVE normalizeCountry() after migration
          state: props.record.event.state,
          county: props.record.event.county,
          town: props.record.event.town,
          hamlet: props.record.event.hamlet,
          postcode: props.record.event.postcode,
          road: props.record.event.road,
          houseNumber: props.record.event.houseNumber,
          coordinatesInferred: props.record.event.coordinatesInferred || false,
          parentEventId: props.record.event.parentEventId || null
        });
        setPartOfMultiHazard(!!props.record.event.parentEventId);
      }
    }
  }, [isEditMode, form, props.record.event]);

  useEffect(() => {
    if (isEditMode && props.record.event) {
      // Manually check validation status when in edit mode after initial render
      const values = form.getFieldsValue();
      const requiredFields = ['name', 'type', 'date'];
      const hasEmptyRequired = requiredFields.some(field => 
        !values[field] || (typeof values[field] === 'string' && values[field].trim() === '')
      );
      
      // Update parent with validation status
      if (hasEmptyRequired) {
        // Create formatted fields for parent callback
        const formattedFields = {};
        Object.keys(values).forEach(key => {
          formattedFields[key] = { value: values[key] };
        });
        props.parentCallback(formattedFields, false);
      }
    }
  }, [isEditMode]); // Only run when edit mode changes

  useEffect(() => {
    if (isEditMode && props.record.event) {
      form.validateFields();
    }
  }, [isEditMode, form, props.record.event]);

  // Load the Multi-Hazard events to choose a parent from (refresh on entering edit mode).
  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/events/", {
        params: { type: 'MULTI_HAZARD_EVENT', size: 1000 }
      })
      .then(({ data }) => setParentEventOptions(data.content || []))
      .catch(error => console.log(error));
  }, [isEditMode]);

  const computeAndNotify = (partOfMH) => {
    const values = form.getFieldsValue();

    // Format for parent component
    const formattedFields = {};
    Object.keys(values).forEach(key => {
      formattedFields[key] = { value: values[key] };
    });

    // Only check validation in edit mode
    let isValid = true;
    if (isEditMode) {
      const errors = form.getFieldsError();
      const hasErrors = errors.some(error => error.errors.length > 0);

      const requiredFields = ['name', 'type', 'date'];
      const hasEmptyRequired = requiredFields.some(field =>
        !values[field] || (typeof values[field] === 'string' && values[field].trim() === '')
      );

      // If flagged as part of a multi-hazard, the parent Multi-Hazard event must be chosen.
      const multiHazardIncomplete = partOfMH && !values.parentEventId;

      isValid = !hasErrors && !hasEmptyRequired && !multiHazardIncomplete;
    }

    props.parentCallback(formattedFields, isValid);
  };

  const handleFormChange = () => computeAndNotify(partOfMultiHazard);

  const handlePartOfMultiHazardChange = (event) => {
    const checked = event.target.checked;
    setPartOfMultiHazard(checked);
    if (!checked) {
      form.setFieldValue('parentEventId', null);
    }
    computeAndNotify(checked);
  };

  // A Multi-Hazard event cannot itself belong to one: hide and clear the parent.
  useEffect(() => {
    if (isMultiHazardEventType && (partOfMultiHazard || form.getFieldValue('parentEventId'))) {
      setPartOfMultiHazard(false);
      form.setFieldValue('parentEventId', null);
      computeAndNotify(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiHazardEventType]);

  const handleGeocode = (result) => {
    // Update geometry with the geocoded coordinates
    if (result.latitude && result.longitude) {
      const geometry = {
        type: "Point",
        coordinates: [result.longitude, result.latitude]
      };
      
      // Convert geometry to JSON string as expected by Map2
      const geometryStr = JSON.stringify(geometry);
      form.setFieldValue('geometry', geometryStr);
      
      // Mark coordinates as inferred since they came from geocoding API
      form.setFieldValue('coordinatesInferred', true);
      
      // Trigger form change to update parent
      handleFormChange();
    }
  };



  return (
    <Form 
      form={form}
      {...formItemLayout}
      onValuesChange={handleFormChange}
      onFieldsChange={handleFormChange}
    >
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item 
            label="Name" 
            name="name"
            rules={isEditMode ? [
              { required: true, message: "Please input Name!" },
              {
                validator: (_, value) => {
                  if (value && value.trim() === '') {
                    return Promise.reject('Name cannot be just whitespace');
                  }
                  return Promise.resolve();
                }
              }
            ] : []}
          >
            <Input disabled={!isEditMode} placeholder="Name" />
          </Form.Item>
          
          <Form.Item 
            label="Type" 
            name="type"
            rules={isEditMode ? [{ required: true, message: "Please input Type!" }] : []}
          >
            <Select disabled={!isEditMode} placeholder="Type" style={{ width: '100%' }}>
              {props.selectOption.types?.map(eventType => (
                <Option key={eventType} value={eventType}>
                  {t(`events.type.${eventType}`)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {!isMultiHazardEventType && (
            <Form.Item label="Multi-hazard" style={{ marginBottom: partOfMultiHazard ? 8 : undefined }}>
              <Checkbox
                checked={partOfMultiHazard}
                disabled={!isEditMode}
                onChange={handlePartOfMultiHazardChange}
              >
                Part of a multi-hazard event
              </Checkbox>
            </Form.Item>
          )}

          {!isMultiHazardEventType && partOfMultiHazard && (
            <Form.Item
              label="Multi-hazard event"
              name="parentEventId"
              rules={isEditMode ? [{ required: true, message: "Please select the multi-hazard event" }] : []}
            >
              <Select
                disabled={!isEditMode}
                placeholder="Select the multi-hazard event"
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
              >
                {parentEventOptions
                  .filter(option => option.id !== props.record.event?.id)
                  .map(option => (
                    <Option key={option.id} value={option.id}>
                      {option.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label="Date"
            name="date"
            rules={isEditMode ? [{ required: true, message: "Please input Date!" }] : []}
          >
            <DatePicker disabled={!isEditMode} placeholder="Date" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label="End Date"
            name="dateEnd"
            rules={isEditMode ? [{ required: false, message: "Please input End Date!" }] : []}
          >
            <DatePicker disabled={!isEditMode} placeholder="End Date" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Address" style={{ marginBottom: 0 }}>
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  name="country"
                  rules={isEditMode ? [{ required: false, message: 'Please select country!' }] : []}
                  getValueFromEvent={(value) => {
                    // If old data has country name, keep it for display but save code
                    return value;
                  }}
                >
                  <Select 
                    placeholder="Country" 
                    disabled={!isEditMode}
                    showSearch
                    optionFilterProp="children"
                  >
                    {(() => {
                      // First, let's see what's actually available in translations
                      const allTranslations = t('translation:countries', { returnObjects: true });
                      console.log('All translations attempt:', allTranslations);
                      
                      // Try to get the entire translation namespace
                      const namespace = t('', { returnObjects: true });
                      console.log('Full namespace:', namespace);
                      
                      const countries = t('countries', { returnObjects: true });
                      console.log('Countries translation result:', countries);
                      console.log('Type of countries:', typeof countries);
                      console.log('Is countries a string?', typeof countries === 'string');
                      // Check if translation loaded properly
                      if (typeof countries === 'string') {
                        // Fallback to hardcoded country list if translation fails
                        console.warn('Countries translation not loaded, using fallback. Got:', countries);
                        const fallbackCountries = {
                          'IT': 'Italy',
                          'FR': 'France',
                          'DE': 'Germany',
                          'ES': 'Spain',
                          'PT': 'Portugal',
                          'NL': 'Netherlands',
                          'BE': 'Belgium',
                          'AT': 'Austria'
                        };
                        return Object.entries(fallbackCountries).map(([code, name]) => (
                          <Option key={code} value={code}>
                            {name}
                          </Option>
                        ));
                      }
                      // Normal case - translation loaded successfully
                      return Object.entries(countries).map(([code, name]) => (
                        <Option key={code} value={code}>
                          {name}
                        </Option>
                      ));
                    })()}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="state"
                  rules={isEditMode ? [{ required: false, message: 'Please input state!' }] : []}
                >
                  <Input placeholder="State" disabled={!isEditMode} />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  name="county"
                  rules={isEditMode ? [{ required: false, message: 'Please input county!' }] : []}
                >
                  <Input placeholder="County" disabled={!isEditMode} />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item
                  name="town"
                  rules={isEditMode ? [{ required: false, message: 'Please input town!' }] : []}
                >
                  <Input placeholder="Town" disabled={!isEditMode} />
                </Form.Item>
              </Col>
              <Col span={2}>
                <Form.Item noStyle shouldUpdate>
                  {() => (
                    <GeocodingButton
                      value={form.getFieldValue('town')}
                      type="city"
                      country={form.getFieldValue('country') || 'IT'}
                      onGeocode={handleGeocode}
                      disabled={!isEditMode}
                    />
                  )}
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  name="hamlet"
                  rules={isEditMode ? [{ required: false, message: 'Please input hamlet!' }] : []}
                >
                  <Input placeholder="Hamlet" disabled={!isEditMode} />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item
                  name="postcode"
                  rules={isEditMode ? [{ required: false, message: 'Please input post code!' }] : []}
                >
                  <Input placeholder="Post code" disabled={!isEditMode} />
                </Form.Item>
              </Col>
              <Col span={2}>
                <Form.Item noStyle shouldUpdate>
                  {() => (
                    <GeocodingButton
                      value={form.getFieldValue('postcode')}
                      type="postcode"
                      country={form.getFieldValue('country') || 'IT'}
                      onGeocode={handleGeocode}
                      disabled={!isEditMode}
                    />
                  )}
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={8}>
              <Col span={16}>
                <Form.Item
                  name="road"
                  rules={isEditMode ? [{ required: false, message: 'Please input road!' }] : []}
                >
                  <Input placeholder="Road" disabled={!isEditMode} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="houseNumber"
                  rules={isEditMode ? [{ required: false, message: 'Please input house number!' }] : []}
                >
                  <Input placeholder="Number" disabled={!isEditMode} />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
        </Col>
        
        <Col span={12}>
          <Form.Item 
            label="Position" 
            name="geometry"
          >
            <Map2 name="event" disabled={!isEditMode} />
          </Form.Item>
          
          <Form.Item
            name="coordinatesInferred"
            valuePropName="checked"
            style={{ marginTop: 8 }}
          >
            <Checkbox disabled={!isEditMode}>
              Coordinates are approximate/inferred (not exact GPS)
            </Checkbox>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default EventForm;